import type { NearbyPeer, PeerId } from '../types';
import {
  findShorterDuplicatePeerIds,
  MIN_BLE_PEER_ID_LENGTH,
  peerIdMatches,
  resolveCanonicalPeerId,
  resolveRosterPeerId,
} from './peerId';
import type { RssiProcessor } from './rssiProcessor';

export type PeerRegistry = {
  peers: Map<PeerId, NearbyPeer>;
  deviceToUser: Map<string, PeerId>;
};

export function createPeerRegistry(): PeerRegistry {
  return {
    peers: new Map(),
    deviceToUser: new Map(),
  };
}

export type ScanObservation = {
  deviceId: string;
  rssi: number;
  advertisedPeerId?: string;
};

export type ScanApplyOutcome =
  | { kind: 'ignored' }
  | { kind: 'discovered'; peer: NearbyPeer }
  | { kind: 'updated'; peer: NearbyPeer };

/**
 * Apply a BLE scan observation to the peer registry (Lynx LynxService.handleScanResult).
 * Returns the updated registry and whether the peer was newly discovered or updated.
 */
export function applyScanObservation(
  registry: PeerRegistry,
  observation: ScanObservation,
  rssiProcessor: RssiProcessor,
  now: number,
  /** Full user ids known from elsewhere (the GPS peers), so a truncated scan resolves to a person. */
  knownPeerIds: Iterable<PeerId> = [],
  /**
   * The full user ids being watched, when known. A Bluetooth id that fits none of them is somebody
   * else's phone and is ignored; null (roster not read yet) falls back to the ids above.
   */
  roster: readonly PeerId[] | null = null,
): { registry: PeerRegistry; outcome: ScanApplyOutcome } {
  const { deviceId, rssi, advertisedPeerId } = observation;

  if (rssi >= 0) {
    return { registry, outcome: { kind: 'ignored' } };
  }

  const raw = advertisedPeerId?.trim() || registry.deviceToUser.get(deviceId);
  if (!raw) {
    return { registry, outcome: { kind: 'ignored' } };
  }
  let peerId: PeerId;
  if (roster !== null) {
    const member = resolveRosterPeerId(raw, roster);
    if (member === null) {
      return { registry, outcome: { kind: 'ignored' } };
    }
    peerId = member;
  } else {
    if (raw.length < MIN_BLE_PEER_ID_LENGTH) {
      return { registry, outcome: { kind: 'ignored' } };
    }
    peerId = resolveCanonicalPeerId(raw, [...registry.peers.keys(), ...knownPeerIds]);
  }

  for (const oldId of findShorterDuplicatePeerIds(peerId, [...registry.peers.keys()])) {
    registry = rekeyPeer(registry, oldId, peerId, rssiProcessor);
  }

  const peers = new Map(registry.peers);
  const deviceToUser = new Map(registry.deviceToUser);

  deviceToUser.set(deviceId, peerId);
  const { smoothedRssi, zone, estimatedDistance } = rssiProcessor.addReading(peerId, rssi, now);

  const existing = peers.get(peerId);
  const peer: NearbyPeer = {
    peerId,
    rssi,
    smoothedRssi,
    estimatedDistance,
    zone,
    lastSeenAt: now,
    ...(existing?.gpsDistance !== undefined ? { gpsDistance: existing.gpsDistance } : {}),
    ...(existing?.gpsBearingDeg !== undefined ? { gpsBearingDeg: existing.gpsBearingDeg } : {}),
    ...(existing?.gpsSource !== undefined ? { gpsSource: existing.gpsSource } : {}),
    ...(existing?.uwbActive !== undefined ? { uwbActive: existing.uwbActive } : {}),
    ...(existing?.uwbDistance !== undefined ? { uwbDistance: existing.uwbDistance } : {}),
    ...(existing?.uwbBearing !== undefined ? { uwbBearing: existing.uwbBearing } : {}),
    ...(existing?.uwbDirectionAvailable !== undefined
      ? { uwbDirectionAvailable: existing.uwbDirectionAvailable }
      : {}),
  };

  if (!existing) {
    peers.set(peerId, peer);
    return {
      registry: { peers, deviceToUser },
      outcome: { kind: 'discovered', peer },
    };
  }

  peers.set(peerId, peer);
  return {
    registry: { peers, deviceToUser },
    outcome: { kind: 'updated', peer },
  };
}

/**
 * Moves a peer from a truncated BLE id to their full user id, keeping what is known about them.
 * Every surface (names, the map's dots, tracking) looks people up by the full id; a registry keyed
 * `134` left Lexie as "Group member", off the map, and untrackable all evening (2026-10-11).
 */
export function rekeyPeer(
  registry: PeerRegistry,
  fromId: PeerId,
  toId: PeerId,
  rssiProcessor: RssiProcessor,
): PeerRegistry {
  const source = registry.peers.get(fromId);
  if (fromId === toId || !source) {
    return registry;
  }
  const peers = new Map(registry.peers);
  peers.delete(fromId);
  const target = peers.get(toId);
  peers.set(toId, target ? { ...source, ...target, peerId: toId } : { ...source, peerId: toId });

  const deviceToUser = new Map(registry.deviceToUser);
  for (const [deviceId, mappedId] of deviceToUser) {
    if (mappedId === fromId) {
      deviceToUser.set(deviceId, toId);
    }
  }
  rssiProcessor.clearDevice(fromId);
  return { peers, deviceToUser };
}

/**
 * One person reached us under two keys (a cut-short Bluetooth id and their full id): keep the newest
 * fix, the Bluetooth reading from whichever entry has one, and the UWB range from whichever is ranging.
 */
export function mergePeerAliases(a: NearbyPeer, b: NearbyPeer, peerId: PeerId): NearbyPeer {
  const [newer, older] = b.lastSeenAt > a.lastSeenAt ? [b, a] : [a, b];
  const merged: NearbyPeer = { ...older, ...newer, peerId, lastSeenAt: newer.lastSeenAt };
  const ble = newer.rssi < 0 ? newer : older.rssi < 0 ? older : null;
  if (ble) {
    merged.rssi = ble.rssi;
    merged.smoothedRssi = ble.smoothedRssi;
    merged.estimatedDistance = ble.estimatedDistance;
    merged.zone = ble.zone;
  }
  const uwb = newer.uwbActive ? newer : older.uwbActive ? older : null;
  if (uwb) {
    merged.uwbActive = true;
    if (uwb.uwbDistance !== undefined) merged.uwbDistance = uwb.uwbDistance;
    if (uwb.uwbBearing !== undefined) merged.uwbBearing = uwb.uwbBearing;
    else delete merged.uwbBearing;
    if (uwb.uwbDirectionAvailable !== undefined)
      merged.uwbDirectionAvailable = uwb.uwbDirectionAvailable;
  }
  return merged;
}

/**
 * Re-files every registry entry under the roster user it belongs to: aliases of one person collapse
 * into one entry, and keys that fit nobody on the roster (another Prii phone nearby, a cut-short id
 * that fits two people) are dropped (2026-10-14 Turnstile, "2 of 1 sharing").
 */
export function canonicalizeRegistryToRoster(
  registry: PeerRegistry,
  roster: readonly PeerId[],
  rssiProcessor: RssiProcessor,
): PeerRegistry {
  let changed = false;
  const peers = new Map<PeerId, NearbyPeer>();
  const keyFor = new Map<PeerId, PeerId | null>();
  for (const [id, peer] of registry.peers) {
    const member = resolveRosterPeerId(id, roster);
    keyFor.set(id, member);
    if (member !== id) {
      changed = true;
      rssiProcessor.clearDevice(id);
    }
    if (member === null) {
      continue;
    }
    const prior = peers.get(member);
    peers.set(member, prior ? mergePeerAliases(prior, peer, member) : { ...peer, peerId: member });
  }
  if (!changed) {
    return registry;
  }
  const deviceToUser = new Map<string, PeerId>();
  for (const [deviceId, mappedId] of registry.deviceToUser) {
    const member = keyFor.has(mappedId)
      ? keyFor.get(mappedId)!
      : resolveRosterPeerId(mappedId, roster);
    if (member !== null) {
      deviceToUser.set(deviceId, member);
    }
  }
  return { peers, deviceToUser };
}

/** Peer ids whose lastSeenAt is older than lostTimeoutMs (Lynx checkLostUsers). */
export function findLostPeerIds(
  registry: PeerRegistry,
  now: number,
  lostTimeoutMs: number,
): PeerId[] {
  const lost: PeerId[] = [];
  for (const [id, peer] of registry.peers) {
    if (now - peer.lastSeenAt > lostTimeoutMs) {
      lost.push(id);
    }
  }
  return lost;
}

export function removePeer(
  registry: PeerRegistry,
  peerId: PeerId,
  rssiProcessor: RssiProcessor,
): PeerRegistry {
  const peers = new Map(registry.peers);
  peers.delete(peerId);

  const deviceToUser = new Map(registry.deviceToUser);
  for (const [deviceId, mappedId] of deviceToUser) {
    if (peerIdMatches(mappedId, peerId)) {
      deviceToUser.delete(deviceId);
    }
  }

  rssiProcessor.clearDevice(peerId);
  return { peers, deviceToUser };
}

export function clearPeerRegistry(rssiProcessor: RssiProcessor): PeerRegistry {
  rssiProcessor.clearAll();
  return createPeerRegistry();
}
